import React, { useState } from 'react';

const ServiceSelectionView = ({ centerType, onSelectService }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingService, setPendingService] = useState(null);
    const [codeInput, setCodeInput] = useState('');
    const styles = {
        container: { padding: '30px', maxWidth: '960px', margin: '0 auto', textAlign: 'center' },
        card: { backgroundColor: '#f7f9fc', borderRadius: '22px', padding: '32px', boxShadow: '0 24px 44px rgba(15, 23, 42, 0.08)' },
        title: { marginBottom: '6px', fontSize: '2rem', color: '#102a43' },
        subtitle: { marginBottom: '28px', color: '#334e68', lineHeight: 1.7, maxWidth: '760px', margin: '0 auto 28px' },
        section: { marginBottom: '28px', textAlign: 'left', backgroundColor: 'white', borderRadius: '18px', padding: '24px', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)' },
        sectionHeader: { fontSize: '1.15rem', fontWeight: '700', marginBottom: '10px', color: '#0d3b66' },
        sectionDescription: { marginBottom: '18px', color: '#3e4c6d', lineHeight: 1.6 },
        buttonGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
        button: { padding: '20px', borderRadius: '16px', border: '1px solid #dde6f2', backgroundColor: 'white', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', fontSize: '1rem', minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '10px', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.04)' },
        buttonPrimary: { backgroundColor: '#0d3b66', color: 'white', borderColor: '#0d3b66' },
        infoBox: { backgroundColor: 'white', borderRadius: '16px', padding: '20px 24px', marginBottom: '24px', border: '1px solid #dbe2ef', color: '#102a43', textAlign: 'left' },
        pill: { display: 'inline-block', margin: '4px 8px 0 0', padding: '6px 12px', borderRadius: '999px', backgroundColor: '#e8f1ff', color: '#0d3b66', fontWeight: 600, fontSize: '0.95rem' }
    };

    const baseServices = [
        { id: 'consultation_ext', label: 'Consultation Extérieure', icon: '🩺' },
        { id: 'dermatologie', label: 'Dermatologie', icon: '🧴' },
        { id: 'ophtalmologie', label: 'Ophtalmologie', icon: '👁️' },
        { id: 'cardiologie', label: 'Cardiologie', icon: '❤️' },
        { id: 'neurologie', label: 'Neurologie', icon: '🧠' }
    ];

    const internalServices = [
        { id: 'dentisterie', label: 'Dentisterie', icon: '🦷' },
        { id: 'pediatrie', label: 'Pédiatrie', icon: '👶' },
        { id: 'orl', label: 'ORL', icon: '👂' },
        { id: 'gyneco', label: 'Gynécologie-Obstétrique', icon: '🤰' },
        { id: 'urologie', label: 'Urologie', icon: '🚻' },
        { id: 'physiotherapie', label: 'Physiothérapie', icon: '🧘' },
        { id: 'pneumologie', label: 'Pneumologie', icon: '🌬️' },
        { id: 'dermatologie', label: 'Dermatologie', icon: '🧴' }
    ];

    const hospitalServices = centerType === 'hospital'
        ? [
            { id: 'hospitalisation', label: 'Hospitalisation (avec lit)' },
            { id: 'observation', label: 'Observation' }
        ]
        : [];

    const healthCenterServices = centerType === 'health_center_bed'
        ? [
            { id: 'observation_72h', label: 'Observation (jusqu\'à 72h)' },
            { id: 'transfer_72h', label: 'Transfert après 72h' }
        ]
        : centerType === 'health_center_no_bed'
            ? [
                { id: 'observation_24h', label: 'Observation (2 à 24h)' },
                { id: 'transfer_24h', label: 'Transfert après 24h' }
            ]
            : [];

    const getCenterLabel = () => {
        if (centerType === 'hospital') return 'Centre Hospitalier';
        if (centerType === 'health_center_bed') return 'Centre de Santé (avec lit)';
        if (centerType === 'health_center_no_bed') return 'Centre de Santé (sans lit)';
        return 'Centre de soins';
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.infoBox}>
                    <div style={{ fontSize: '1rem', color: '#102a43', marginBottom: '10px', fontWeight: 700 }}>Type d'établissement :</div>
                    <div style={styles.pill}>{getCenterLabel()}</div>
                    <p style={{ margin: '18px 0 0', color: '#334e68', lineHeight: 1.75 }}>
                        Choisissez un service principal. Un centre hospitalier inclut systématiquement l'hospitalisation et l'observation.
                        Pour un centre de santé sans lit, l'observation se fait entre 2 et 24h, puis transfert. Pour un centre de santé avec lit, l'observation peut aller jusqu'à 72h ou plus, puis transfert.
                    </p>
                </div>

                <div style={styles.section}>
                    <div style={styles.sectionHeader}>Consultations et spécialités</div>
                    <div style={styles.sectionDescription}>Services de consultation, spécialités médicales et suivi des patients.</div>
                    <div style={styles.buttonGrid}>
                        {baseServices.map(service => (
                            <button key={service.id} type="button" style={styles.button} onClick={() => { setPendingService(service); setModalOpen(true); }}>
                                <span style={{ fontSize: '1.5rem' }}>{service.icon}</span>
                                <span>{service.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div style={styles.section}>
                    <div style={styles.sectionHeader}>Clinique interne</div>
                    <div style={styles.sectionDescription}>Services médicaux internes et spécialités pour la prise en charge ciblée.</div>
                    <div style={styles.buttonGrid}>
                        {internalServices.map(service => (
                            <button key={service.id} type="button" style={styles.button} onClick={() => { setPendingService(service); setModalOpen(true); }}>
                                <span style={{ fontSize: '1.5rem' }}>{service.icon}</span>
                                <span>{service.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {hospitalServices.length > 0 && (
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>Hospitalisation et observation</div>
                        <div style={styles.sectionDescription}>Pour les centres hospitaliers, chaque service peut être complété par une hospitalisation ou une prise en charge sans lit.</div>
                        <div style={styles.buttonGrid}>
                            {hospitalServices.map(service => (
                                <button key={service.id} type="button" style={{ ...styles.button, backgroundColor: '#e8f1ff', borderColor: '#b3c7e6' }} onClick={() => { setPendingService(service); setModalOpen(true); }}>{service.label}</button>
                            ))}
                        </div>
                    </div>
                )}

                {healthCenterServices.length > 0 && (
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>Observation Centre de Santé</div>
                        <div style={styles.sectionDescription}>Pour les centres de santé, choisissez le mode d'observation adapté aux patients.</div>
                        <div style={styles.buttonGrid}>
                            {healthCenterServices.map(service => (
                                <button key={service.id} type="button" style={{ ...styles.button, backgroundColor: '#f6fbf4', borderColor: '#c8dfc7' }} onClick={() => { setPendingService(service); setModalOpen(true); }}>{service.label}</button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {modalOpen && pendingService && (
                <div style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'white', padding: 20, borderRadius: 8, minWidth: 320 }}>
                        <h3>Code requis pour «{pendingService.label}»</h3>
                        <p>Veuillez saisir le code d'accès fourni pour ce service.</p>
                        <input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Code d'accès" style={{ width: '100%', padding: 8, marginBottom: 12 }} />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => { setModalOpen(false); setCodeInput(''); setPendingService(null); }}>Annuler</button>
                            <button type="button" onClick={() => {
                                onSelectService(pendingService.label, codeInput);
                                setModalOpen(false);
                                setCodeInput('');
                                setPendingService(null);
                            }}>Valider</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceSelectionView;
